import { useState, useEffect, useCallback, useMemo } from "react";
import { save } from "../../shared/stamps";

/**
 * A binder you can lay out before you commit sleeves to it.
 *
 * Slots are keyed by their position counted straight through the whole binder,
 * not by page. That is what makes changing the page size behave the way the
 * real thing does: move from 3x3 to 4x4 and the cards stay in the order you put
 * them in and reflow across the pages, rather than scattering.
 */

const BINDER_KEY = "ppt_binder_v1";

// The sizes pages actually come in. 3x3 is the common one and the default.
export const GRIDS = [
  { cols: 2, rows: 2, label: "2×2" },
  { cols: 3, rows: 3, label: "3×3" },
  { cols: 4, rows: 3, label: "4×3" },
  { cols: 4, rows: 4, label: "4×4" },
];

export const MAX_PAGES = 60;

const EMPTY = { name: "My binder", cols: 3, rows: 3, pages: 8, slots: {} };

function load() {
  try {
    const raw = window.localStorage.getItem(BINDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      ...EMPTY,
      ...parsed,
      slots: parsed.slots && typeof parsed.slots === "object" ? parsed.slots : {},
    };
  } catch {
    return null;
  }
}

export default function useBinder() {
  const [binder, setBinder] = useState(EMPTY);
  /* State rather than a ref, for the same reason the lists use state: a ref
     flips inside the load effect and the save effect then runs in that same
     commit still holding the empty starting value, writing it over everything
     that was saved. */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const saved = load();
    if (saved) setBinder(saved);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      save(BINDER_KEY, JSON.stringify(binder));
    } catch {
      /* a binder that cannot be saved still lays out fine in this session */
    }
  }, [binder, ready]);

  const perPage = binder.cols * binder.rows;
  const capacity = perPage * binder.pages;

  const place = useCallback((index, card) => {
    setBinder((b) => ({ ...b, slots: { ...b.slots, [index]: card } }));
  }, []);

  const clear = useCallback((index) => {
    setBinder((b) => {
      const slots = { ...b.slots };
      delete slots[index];
      return { ...b, slots };
    });
  }, []);

  /** Swap, not insert. Two cards trading places is what you do at a table, and
   *  it never shifts everything after them by one. */
  const swap = useCallback((a, b2) => {
    if (a === b2) return;
    setBinder((b) => {
      const slots = { ...b.slots };
      const from = slots[a];
      const to = slots[b2];
      if (to) slots[a] = to;
      else delete slots[a];
      if (from) slots[b2] = from;
      else delete slots[b2];
      return { ...b, slots };
    });
  }, []);

  const setGrid = useCallback((cols, rows) => {
    setBinder((b) => ({ ...b, cols, rows }));
  }, []);

  const setPages = useCallback((n) => {
    setBinder((b) => ({ ...b, pages: Math.max(1, Math.min(MAX_PAGES, Number(n) || 1)) }));
  }, []);

  const rename = useCallback((name) => setBinder((b) => ({ ...b, name })), []);

  /* Replaces outright rather than merging. Merging two layouts means deciding
     what happens where they collide, and there is no answer to that which is
     not a surprise to somebody. */
  const replace = useCallback((next) => setBinder({ ...EMPTY, ...next }), []);

  /* Cards past the last page. Shrinking the binder or the page size does not
     throw them away — they are held, counted and reported, because silently
     dropping cards someone placed is the one thing this must not do. */
  const overflow = useMemo(
    () => Object.keys(binder.slots).map(Number).filter((i) => i >= capacity).length,
    [binder.slots, capacity]
  );

  const filled = useMemo(
    () => Object.keys(binder.slots).map(Number).filter((i) => i < capacity).length,
    [binder.slots, capacity]
  );

  const value = useMemo(
    () =>
      Object.entries(binder.slots)
        .filter(([i]) => Number(i) < capacity)
        .reduce((sum, [, c]) => sum + (Number(c?.price) || 0), 0),
    [binder.slots, capacity]
  );

  return {
    binder, ready, perPage, capacity, filled, overflow, value,
    place, clear, swap, setGrid, setPages, rename, replace,
  };
}
